/**
 * Monster Onesie — collection catalog filter + sort.
 *
 * Drives sections/monster-colorway-grid.liquid from
 * sections/monster-collection-controls.liquid. The two are separate sections
 * because they are separate design objects — one sticks, one scrolls — so the
 * component finds the grid the same way monster-header-pin.js finds the hero:
 * a document-level query for a data attribute. No grid on the page means no
 * controls, and that is the correct degradation.
 *
 * Everything is client-side over server-rendered cards. There is no fetch and
 * no re-render: all seven colourways are already in the DOM with their family,
 * name, swatch brightness and fits on data attributes, so filtering is a
 * `hidden` toggle and sorting is a CSS `order` write. Nothing can arrive
 * half-loaded and no state can disagree with what Liquid rendered.
 *
 * Two rules worth keeping:
 *
 *   1. SORTING IS NEVER HARDCODED. Lightest/darkest read `data-brightness`,
 *      which Liquid computed with `color_brightness` from the option value's
 *      own admin swatch. Restyle a colour in Products and the sort reorders
 *      itself. There is no manual order field and there must not be one.
 *
 *   2. THE DUO PANEL IS NOT A COLOURWAY. It always sorts last and hides
 *      whenever any filter is active — leaving a bundle in a filtered result
 *      would answer a question nobody asked, and it must never be counted.
 */

const STAGGER_MS = 45;

/** The Duo panel's order value: past any plausible number of colourways. */
const DUO_ORDER = 9999;

class MonsterCatalogControls extends HTMLElement {
  /** @type {HTMLElement | null} */
  #grid = null;

  /** @type {HTMLElement[]} */
  #cards = [];

  /** @type {HTMLElement | null} */
  #duo = null;

  /** @type {Set<string>} */
  #families = new Set();

  /** @type {Set<string>} */
  #fits = new Set();

  #sort = 'featured';

  #strings = { countAll: '[total] colorways', countSome: '[n] of [total] colorways' };

  connectedCallback() {
    this.#grid = document.querySelector('[data-catalog-grid]');
    if (!this.#grid) return;

    this.#cards = Array.from(this.#grid.querySelectorAll('[data-card]'));
    if (!this.#cards.length) return;

    this.#duo = this.#grid.querySelector('[data-duo]');

    this.#readStrings();
    this.#buildFamilyChips();
    this.#bindFits();
    this.#bindClear();
    this.#bindSort();

    // Featured order is the order Liquid rendered, which is the product's own
    // Color option order. Recorded once so "Featured" can always return to it.
    this.#cards.forEach((card, index) => {
      card.dataset.featured = String(index);
    });

    this.#render();
  }

  #readStrings() {
    const node = this.querySelector('[data-catalog-strings]');
    if (!node?.textContent) return;
    try {
      Object.assign(this.#strings, JSON.parse(node.textContent));
    } catch {
      // Keep the defaults. A malformed string payload must not cost the
      // shopper the filters.
    }
  }

  /** The grid publishes which families are in use and whether kids exist. */
  #data() {
    const node = this.#grid?.querySelector('[data-catalog-data]');
    if (!node?.textContent) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  /**
   * Family chips come from the grid's payload, never from a list in here.
   * A chip is therefore only ever offered for a family some colourway
   * actually uses, and its dot is a real admin swatch from that family
   * rather than a brand hex typed into the theme.
   */
  #buildFamilyChips() {
    const data = this.#data();
    const families = data?.families ?? [];
    const group = this.querySelector('[data-group="family"]');
    const host = this.querySelector('[data-family-chips]');
    if (!group || !host) return;

    // One family is not a filter — it selects everything or nothing.
    if (families.length < 2) return;

    families.forEach(({ name, swatch }) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'monster-chip';
      chip.dataset.familyValue = name;
      chip.setAttribute('aria-pressed', 'false');

      if (swatch && swatch !== 'transparent') {
        const dot = document.createElement('span');
        dot.className = 'monster-chip__swatch';
        dot.style.background = swatch;
        chip.append(dot);
      }
      chip.append(document.createTextNode(name));

      chip.addEventListener('click', () => {
        this.#toggle(this.#families, name, chip);
        this.#render();
      });

      host.append(chip);
    });

    group.hidden = false;
  }

  /**
   * Fits is withheld entirely until kids variants exist. A group whose only
   * selectable value is "everything" is decoration, and the whole point of
   * building the bar now is that switching it on later is data, not code.
   */
  #bindFits() {
    const data = this.#data();
    const group = this.querySelector('[data-group="fits"]');
    if (!group || !data?.hasKids) return;

    group.querySelectorAll('[data-fits-value]').forEach((chip) => {
      chip.setAttribute('aria-pressed', 'false');
      chip.addEventListener('click', () => {
        this.#toggle(this.#fits, chip.dataset.fitsValue, chip);
        this.#render();
      });
    });

    group.hidden = false;
  }

  #bindClear() {
    // Both the bar's Clear and the empty state's Clear, so a shopper who has
    // filtered everything away can recover without scrolling back up.
    document.querySelectorAll('[data-catalog-clear]').forEach((button) => {
      button.addEventListener('click', () => this.#clear());
    });
  }

  #bindSort() {
    const select = this.querySelector('[data-catalog-sort]');
    if (!select) return;
    select.addEventListener('change', () => {
      this.#sort = select.value;
      this.#render();
    });
  }

  /**
   * @param {Set<string>} set
   * @param {string} value
   * @param {HTMLElement} chip
   */
  #toggle(set, value, chip) {
    if (set.has(value)) {
      set.delete(value);
      chip.setAttribute('aria-pressed', 'false');
    } else {
      set.add(value);
      chip.setAttribute('aria-pressed', 'true');
    }
  }

  #clear() {
    this.#families.clear();
    this.#fits.clear();
    this.querySelectorAll('.monster-chip').forEach((chip) => chip.setAttribute('aria-pressed', 'false'));
    this.#render();
  }

  get #filtered() {
    return this.#families.size > 0 || this.#fits.size > 0;
  }

  /** @param {HTMLElement} card */
  #matches(card) {
    if (this.#families.size && !this.#families.has(card.dataset.family)) return false;

    if (this.#fits.size) {
      const fits = (card.dataset.fits || '').split(/\s+/).filter(Boolean);
      if (!fits.some((fit) => this.#fits.has(fit))) return false;
    }

    return true;
  }

  #render() {
    const visible = this.#cards.filter((card) => this.#matches(card));
    const order = this.#sorted(visible);

    this.#cards.forEach((card) => {
      card.hidden = !visible.includes(card);
    });

    // Stagger runs down the VISUAL order, not the DOM order, so a re-sort
    // cascades in the direction the eye is about to read.
    order.forEach((card, index) => {
      card.style.order = String(index);
      card.style.setProperty('--monster-stagger', `${index * STAGGER_MS}ms`);
    });

    if (this.#duo) {
      this.#duo.hidden = this.#filtered;
      this.#duo.style.order = String(DUO_ORDER);
      this.#duo.style.setProperty('--monster-stagger', `${order.length * STAGGER_MS}ms`);
    }

    const empty = this.#grid?.querySelector('[data-catalog-empty]');
    if (empty) empty.hidden = order.length > 0;

    const clear = this.querySelector('[data-catalog-clear]');
    if (clear) clear.hidden = !this.#filtered;

    this.#renderCount(order.length);
    this.#replay(order);
  }

  /** @param {HTMLElement[]} cards */
  #sorted(cards) {
    const list = [...cards];
    const name = (card) => card.dataset.name || '';
    const lum = (card) => Number(card.dataset.brightness) || 0;

    switch (this.#sort) {
      case 'az':
        return list.sort((a, b) => name(a).localeCompare(name(b)));
      case 'za':
        return list.sort((a, b) => name(b).localeCompare(name(a)));
      case 'light':
        return list.sort((a, b) => lum(b) - lum(a));
      case 'dark':
        return list.sort((a, b) => lum(a) - lum(b));
      default:
        return list.sort((a, b) => Number(a.dataset.featured) - Number(b.dataset.featured));
    }
  }

  /** @param {number} shown */
  #renderCount(shown) {
    const node = this.querySelector('[data-catalog-count]');
    if (!node) return;

    const total = this.#cards.length;
    const template = shown === total ? this.#strings.countAll : this.#strings.countSome;

    node.textContent = String(template)
      .replace('[n]', String(shown))
      .replace('[total]', String(total));
  }

  /**
   * Restart the rise animation on the cards that are now showing.
   *
   * Reassigning animation-delay alone does nothing — the animation has
   * already finished and the browser will not replay a completed one. The
   * cheap, reliable restart is to cancel any running animations and force a
   * style recalculation between removing and re-adding the name.
   *
   * @param {HTMLElement[]} cards
   */
  #replay(cards) {
    const targets = this.#duo && !this.#duo.hidden ? [...cards, this.#duo] : cards;

    targets.forEach((card) => {
      card.style.animationName = 'none';
    });

    // One forced reflow for the whole batch rather than one per card.
    void this.offsetHeight;

    targets.forEach((card) => {
      card.style.animationName = '';
    });
  }
}

if (!customElements.get('monster-catalog-controls')) {
  customElements.define('monster-catalog-controls', MonsterCatalogControls);
}
